import type { MetricsSample } from '@/app/rate-limit-metrics/types';
import type { ChartPairConfig, WorkloadInsightState } from '@/app/workload-metrics/types';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';

export const METRICS_SNAPSHOT_KIND = 'urm-metrics-snapshot';
export const METRICS_SNAPSHOT_VERSION = 2;

export type MetricsSnapshotTab = 'workload' | 'rate-limit';

export interface WorkloadMetricsSnapshot {
  intervals: WorkloadMetricsInterval[];
  chartPairs: ChartPairConfig[];
  hiddenSeriesByPair: Record<number, string[]>;
  insightState: WorkloadInsightState;
  lastUpdatedMs: number;
  loadedFromMs: number;
  loadedToMs: number;
}

export interface RateLimitMetricsSnapshot {
  samples: MetricsSample[];
  selectedVariants: string[];
  hiddenPods: string[];
  downsamplingEnabled: boolean;
  lastUpdatedMs: number;
  loadedFromMs: number;
  loadedToMs: number;
}

export interface MetricsSnapshot {
  kind: typeof METRICS_SNAPSHOT_KIND;
  version: typeof METRICS_SNAPSHOT_VERSION;
  createdAtMs: number;
  activeTab: MetricsSnapshotTab;
  range: {
    fromMs: number;
    toMs: number;
    presetLabel: string | null;
  };
  workload: WorkloadMetricsSnapshot;
  rateLimit: RateLimitMetricsSnapshot;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

const GROUPING_DIMENSIONS = new Set(['username', 'resourceType', 'action', 'apiLabel']);
const LATENCY_METRICS = new Set([
  'totalRequests',
  'processingSharePercent',
  'averageDurationMs',
  'histogramP50Ms',
  'histogramP95Ms',
  'histogramP99Ms',
  'slowerThan250Percent',
  'slowerThan1000Percent',
]);

function isGroupingDimensions(value: unknown, maximumLength: number): boolean {
  return Array.isArray(value)
    && value.length >= 1
    && value.length <= maximumLength
    && value.every((dimension) => GROUPING_DIMENSIONS.has(String(dimension)));
}

function isWorkloadInsightState(value: unknown): boolean {
  return isRecord(value)
    && isGroupingDimensions(value.dimensions, 2)
    && isGroupingDimensions(value.latencyDimensions, 3)
    && !(value.latencyDimensions as unknown[]).includes('username')
    && LATENCY_METRICS.has(String(value.latencyMetric));
}

function requireSnapshotShape(value: unknown): asserts value is MetricsSnapshot {
  if (!isRecord(value)) throw new Error('The selected file is not a metrics snapshot.');
  if (value.kind !== METRICS_SNAPSHOT_KIND) {
    throw new Error('The selected file is not a URM metrics snapshot.');
  }
  if (value.version !== METRICS_SNAPSHOT_VERSION) {
    throw new Error(`Unsupported metrics snapshot version: ${String(value.version)}.`);
  }
  if (!isFiniteNumber(value.createdAtMs)) throw new Error('The snapshot timestamp is invalid.');
  if (value.activeTab !== 'workload' && value.activeTab !== 'rate-limit') {
    throw new Error('The snapshot dashboard tab is invalid.');
  }
  if (!isRecord(value.range)
    || !isFiniteNumber(value.range.fromMs)
    || !isFiniteNumber(value.range.toMs)
    || value.range.toMs <= value.range.fromMs
    || (value.range.presetLabel !== null && typeof value.range.presetLabel !== 'string')) {
    throw new Error('The snapshot time range is invalid.');
  }
  if (!isRecord(value.workload)
    || !Array.isArray(value.workload.intervals)
    || !Array.isArray(value.workload.chartPairs)
    || !isRecord(value.workload.hiddenSeriesByPair)
    || !isWorkloadInsightState(value.workload.insightState)
    || !isFiniteNumber(value.workload.lastUpdatedMs)
    || !isFiniteNumber(value.workload.loadedFromMs)
    || !isFiniteNumber(value.workload.loadedToMs)) {
    throw new Error('The workload snapshot data is invalid.');
  }
  if (!isRecord(value.rateLimit)
    || !Array.isArray(value.rateLimit.samples)
    || !Array.isArray(value.rateLimit.selectedVariants)
    || !Array.isArray(value.rateLimit.hiddenPods)
    || typeof value.rateLimit.downsamplingEnabled !== 'boolean'
    || !isFiniteNumber(value.rateLimit.lastUpdatedMs)
    || !isFiniteNumber(value.rateLimit.loadedFromMs)
    || !isFiniteNumber(value.rateLimit.loadedToMs)) {
    throw new Error('The rate-limit snapshot data is invalid.');
  }
}

export function createMetricsSnapshotJson(snapshot: MetricsSnapshot): string {
  return JSON.stringify(snapshot);
}

export function parseMetricsSnapshotJson(text: string): MetricsSnapshot {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('The selected file is not valid JSON.');
  }
  requireSnapshotShape(parsed);
  return parsed;
}

export function metricsSnapshotFilename(createdAtMs: number): string {
  return `urm-metrics-snapshot-${new Date(createdAtMs).toISOString().replaceAll(':', '-')}.json`;
}

export function downloadMetricsSnapshot(snapshot: MetricsSnapshot): void {
  const blob = new Blob([createMetricsSnapshotJson(snapshot)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = metricsSnapshotFilename(snapshot.createdAtMs);
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
